import { useCallback, useContext, useEffect, useState, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridListViewColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Box, Checkbox, Stack, Toolbar, Typography } from '@mui/material';
import { csCZ, enUS, hrHR, huHU, itIT, roRO, skSK } from '@mui/x-data-grid/locales';

import { SearchComponent } from '@/components/common/Search';
import { ctxTachograph } from '@/contexts/TachographCtx';
import { ctxPopup } from '@/contexts/PopupContext';
import { TachographDriverResponseDTO } from '@/generated/model';
import { getTachographDriverData } from '@/generated/client/tachograph/tachograph';

import './TachographPopup.css';
import { LastDownloadedCell } from './TachographVehicleData';
import TachographEmailButton from './TachographEmailButton';
import { TachoTabs } from './TachographPopup';
import { ToolbarSortItem } from '@/components/common/ToolbarSortItem';

export default function TachographDriverData({
  isListView,
  activeTab,
}: {
  isListView: boolean;
  activeTab?: TachoTabs;
}) {
  const { t, i18n } = useTranslation();
  const tachographCtx = useContext(ctxTachograph);
  const popupCtx = useContext(ctxPopup);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [filteredTachographData, setFilteredTachographData] = useState<TachographDriverResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [selectAll, setSelectAll] = useState(false);
  const dataRef = useRef<HTMLDivElement | null>(null);
  const pageSize = 50;

  const totalRows = filteredTachographData.length;
  const selectedCount = tachographCtx?.selectedRowCardNumbers.length ?? 0;
  const isIndeterminate = selectedCount > 0 && (selectedCount < totalRows || selectedCount === totalRows);

  useEffect(() => {
    if (!isListView) {
      dataRef.current!.style.width = `calc(100vw - ${popupCtx?.drawerMenuOpen ? 300 : 100}px`;
    }
  }, [popupCtx?.drawerMenuOpen]);

  const toDriverRow = (row: TachographDriverResponseDTO) => ({
    id: row.id,
    driverCardNumber: row.driverCardNumber,
    driverName: row.driverName,
    customer: row.customer,
    timeMillis: row.timeMillis,
  });

  const updateTachographData = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    async (pageT: number, pageSizeT: number, nowHasMore: boolean) => {
      if (!tachographCtx?.selectedDates || !nowHasMore || loading) return;
      setLoading(true);

      const timeMillis = tachographCtx.selectedDates.from.getTime();

      const request = {
        keyword: searchText,
        fromTimeMillis: timeMillis,
        page: pageT,
        pageSize: pageSizeT,
      };

      const response = await getTachographDriverData(request);

      const formatted = response.data.map((row) => toDriverRow(row));

      if (response.data.length < pageSize) setHasMore(false);
      setLoading(false);

      // TODO error handling
      setFilteredTachographData((prevRows) => [...prevRows, ...formatted]);
      setPage((prevPage) => prevPage + 1);
    },
    [searchText, tachographCtx?.selectedDates]
  );

  useEffect(() => {
    setFilteredTachographData([]);
    setHasMore(true);
    setPage(1);
    void updateTachographData(1, pageSize, true);
  }, [searchText, updateTachographData]);

  useEffect(() => {
    if (tachographCtx?.selectedRowCardNumbers.length === 0) {
      setSelectAll(false);
    }
  }, [tachographCtx?.selectedRowCardNumbers]);

  if (!tachographCtx) return;

  // FIXME a Localization-t nem tudja importalni :(
  const localeMap: Record<string, typeof enUS> = {
    en: enUS,
    hu: huHU,
    ro: roRO,
    sk: skSK,
    cz: csCZ,
    hr: hrHR,
    sr: enUS,
    sl: enUS,
    it: itIT,
  };

  function handleTemplateSelection(templateId: string) {
    tachographCtx?.setSelectedRowCardNumbers((prev) =>
      prev.includes(templateId) ? prev.filter((rowId) => rowId !== templateId) : [...prev, templateId]
    );
  }

  async function loadMoreRows() {
    await updateTachographData(page, pageSize, hasMore);
  }

  function handleSelectAllChange(event: ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    setSelectAll(checked);

    if (checked) {
      tachographCtx?.setSelectedRowCardNumbers(
        filteredTachographData
          .filter((row) => row.timeMillis > 0)
          .map((row) => `${row.customer}_${row.driverCardNumber}`)
      );
    } else {
      tachographCtx?.setSelectedRowCardNumbers([]);
    }
  }

  function ListViewCell(params: GridRenderCellParams<TachographDriverResponseDTO>) {
    const isChecked =
      tachographCtx?.selectedRowCardNumbers.includes(`${params.row.customer}_${params.row.driverCardNumber}`) &&
      params.row.timeMillis > 0;

    return (
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignItems: 'center',
          height: '100%',
          gap: 2,
          paddingY: 1,
        }}
      >
        <Checkbox
          checked={isChecked}
          onChange={() => handleTemplateSelection(`${params.row.customer}_${params.row.driverCardNumber}`)}
          className={isChecked ? 'tracking-body-target-list-item-selected' : ''}
          sx={{ marginLeft: '5px' }}
        />

        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600} fontSize="14px">
            {params.row.driverName}
          </Typography>
          <Typography variant="body2">{params.row.customer}</Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row.driverCardNumber.replace(/^.*?-/, '')}
          </Typography>
        </Stack>

        <LastDownloadedCell value={params.row.timeMillis} />
      </Stack>
    );
  }

  const listViewColDef: GridListViewColDef<TachographDriverResponseDTO> = {
    field: 'listView',
    renderCell: ListViewCell,
  };

  function SelectAllAndOrderControl() {
    return (
      <div className="selector-toolbar">
        <span>
          <Checkbox
            checked={selectAll}
            disabled={loading}
            indeterminate={isIndeterminate}
            onChange={handleSelectAllChange}
            className={selectAll || isIndeterminate ? 'tracking-body-target-list-item-selected' : ''}
          />
          <span style={{ fontWeight: 600 }}>
            ({filteredTachographData.length}/{tachographCtx?.selectedRowCardNumbers.length})
          </span>
        </span>
        <ToolbarSortItem listView={isListView} />
      </div>
    );
  }

  function getColumnHeaders() {
    return {
      toolbar: () => (
        <Toolbar style={{ minHeight: isListView ? '120px' : '50px', padding: 0 }}>
          <div className="selector-header-container">
            <div className="report-search-field">
              <SearchComponent
                searchText={searchText}
                setSearchText={setSearchText}
                showHistory={false}
                placeholder={t('tachograph.filter.vehicleSearchPlaceholder')}
              />
            </div>
            {isListView && <SelectAllAndOrderControl />}
          </div>
        </Toolbar>
      ),
    };
  }

  const columns = [
    {
      field: 'select',
      renderHeader: () => {
        const totalRows = filteredTachographData.length;
        const selectedCount = tachographCtx?.selectedRowCardNumbers.length ?? 0;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={selectAll}
              disabled={loading}
              indeterminate={selectedCount > 0 && selectedCount < totalRows}
              onChange={handleSelectAllChange}
              className={
                selectAll || (selectedCount > 0 && selectedCount < totalRows)
                  ? 'tracking-body-target-list-item-selected'
                  : ''
              }
            />
            <span style={{ fontWeight: 600 }}>
              ({filteredTachographData.length}/{tachographCtx?.selectedRowCardNumbers.length})
            </span>
          </Box>
        );
      },
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isChecked =
          tachographCtx?.selectedRowCardNumbers.includes(`${params.row.customer}_${params.row.driverCardNumber}`) &&
          params.row.timeMillis > 0;
        return (
          <Checkbox
            checked={isChecked}
            onChange={() => handleTemplateSelection(`${params.row.customer}_${params.row.driverCardNumber}`)}
            className={isChecked ? 'tracking-body-target-list-item-selected' : ''}
            sx={{ marginLeft: '10px' }}
          />
        );
      },
    },
    {
      field: 'driverCardNumber',
      headerName: t('tachograph.table.driverCard'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.value.replace(/^.*?-/, '');
      },
    },
    { field: 'driverName', headerName: t('tachograph.table.name'), flex: 1 },
    { field: 'customer', headerName: t('tachograph.table.customer'), flex: 1 },
    {
      field: 'timeMillis',
      headerName: t('tachograph.table.lastDownloadedData'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <LastDownloadedCell value={params.value} />,
    },
  ];

  return (
    <>
      <DataGridPro
        ref={dataRef}
        rows={filteredTachographData}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterRowCount
        listView={isListView}
        listViewColumn={listViewColDef}
        hideFooter={isListView}
        onRowsScrollEnd={loadMoreRows}
        autosizeOptions={{
          columns: ['select', 'driverCardNumber', 'driverName', 'customer', 'timeMillis'],
        }}
        paginationMode="server"
        getRowClassName={(params) => (params.row.timeMillis < 1 ? 'disabled-row' : '')}
        getRowId={(row) => `${row.customer}_${row.driverCardNumber}`}
        loading={loading}
        getRowHeight={() => 'auto'}
        showToolbar
        slots={getColumnHeaders()}
        disableDensitySelector
        disableColumnFilter
        pageSizeOptions={[100]}
        localeText={{
          ...localeMap[i18n.language].components.MuiDataGrid.defaultProps.localeText,
          noRowsLabel: t('tachograph.table.noData'),
        }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-footerContainer': {
            boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
            boxShadow: 'none',
          },
        }}
      />
      {isListView && tachographCtx.selectedRowCardNumbers.length > 0 && <TachographEmailButton activeTab={activeTab} />}
    </>
  );
}
